import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { AuthLayout } from "./AuthLayout";
import { useLogin } from "@/api/hooks/auth";
import { isApiError } from "@/api/errors";
import { Field, FormError, FormSuccess } from "@/components/Field";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { safeNext } from "@/lib/safeNext";

const schema = z.object({
  email: z.string().trim().min(1, "auth.emailRequired").email("auth.emailInvalid"),
  password: z.string().min(1, "auth.passwordRequired"),
});
type Values = z.infer<typeof schema>;

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const login = useLogin();
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { email: "", password: "" } });
  const { errors } = form.formState;

  const onSubmit = form.handleSubmit((values) => {
    login.mutate(values, { onSuccess: () => navigate(safeNext(params.get("next")), { replace: true }) });
  });

  let serverError: string | null = null;
  if (login.isError) {
    const e = login.error;
    const status = isApiError(e) ? e.status : null;
    serverError =
      status === 429 ? t("auth.tooMany") : status === 401 ? t("auth.incorrect") : status === 422 ? t("auth.emailInvalid") : t("errors.generic");
  }

  return (
    <AuthLayout title={t("auth.loginTitle")} intro={t("auth.loginSubtitle")}>
      <form noValidate onSubmit={onSubmit} className="flex flex-col gap-5">
        {params.get("reset") === "1" ? <FormSuccess message={t("auth.passwordChangedSignIn")} /> : null}
        <FormError message={serverError} />
        <Field label={t("auth.email")} error={errors.email?.message ? t(errors.email.message) : null}>
          {(p) => <Input {...p} type="email" autoComplete="email" {...form.register("email")} />}
        </Field>
        <Field label={t("auth.password")} error={errors.password?.message ? t(errors.password.message) : null}>
          {(p) => <Input {...p} type="password" autoComplete="current-password" {...form.register("password")} />}
        </Field>
        <Button type="submit" className="w-full" disabled={login.isPending}>
          {login.isPending ? t("auth.signingIn") : t("auth.signIn")}
        </Button>
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <Link to="/forgot-password" className="font-medium underline">
            {t("auth.forgotLink")}
          </Link>
          {import.meta.env.VITE_ALLOW_SELF_SIGNUP === "true" ? (
            <span className="text-fg-muted">
              {t("auth.noAccount")}{" "}
              <Link to="/signup" className="font-medium underline">
                {t("auth.signupLink")}
              </Link>
            </span>
          ) : null}
        </div>
      </form>
    </AuthLayout>
  );
}
